// Runtime environment configuration utility
// Reads from window.__ENV__ (injected at container startup) with fallback to process.env

declare global {
  interface Window {
    __ENV__?: Record<string, string>;
  }
}

/**
 * Get environment variable value
 * Priority: runtime (window.__ENV__) > build-time (process.env)
 * 
 * @param key - Environment variable name (e.g., "REACT_APP_CHAIN_ID")
 * @returns The value or undefined
 */
export function getEnv(key: string): string | undefined {
  // Runtime config (from env-config.js injected at container startup)
  if (typeof window !== "undefined" && window.__ENV__) {
    const runtimeValue = window.__ENV__[key];
    if (runtimeValue !== undefined && runtimeValue !== "") {
      return runtimeValue;
    }
  }
  // Fallback to build-time env (for local development with yarn start)
  return process.env[key];
}

/**
 * Get environment variable with a default value
 * 
 * @param key - Environment variable name
 * @param defaultValue - Default value if not found
 * @returns The value or default
 */
export function getEnvOrDefault(key: string, defaultValue: string): string {
  return getEnv(key) ?? defaultValue;
}

/**
 * Get environment variable as integer
 * 
 * @param key - Environment variable name
 * @param defaultValue - Default value if not found or invalid
 * @returns The parsed integer or default
 */
export function getEnvInt(key: string, defaultValue: number): number {
  const value = getEnv(key);
  if (value === undefined) return defaultValue;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : parsed;
}

/**
 * Get environment variable as boolean
 * 
 * @param key - Environment variable name
 * @param defaultValue - Default value if not found
 * @returns true if value is "true" (case-insensitive), false otherwise
 */
export function getEnvBool(key: string, defaultValue: boolean = false): boolean {
  const value = getEnv(key);
  if (value === undefined) return defaultValue;
  return value.toLowerCase() === "true";
}
